import { useTranslation } from 'react-i18next';
import {
  Banner,
  Button,
  ColorsV2,
  Flex,
  spaces,
  Text,
  useThemeColors,
} from '@electricjs/arc';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  EntitlementSlugEnum,
  PermissionEnum,
} from '@electricjs/core_entity-client';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { getLocalizedSupportUrl } from '@/helpers/getLocalizedSupportUrl';
import { Asset } from '@/types/assets';
import MDMInviteModalWrapper from '@/components/Organization/MDMInviteModal/MDMInviteModalWrapper';
import { useGetEmployeeQuery } from '@/redux/slices/employeeApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useUpdateMdmStatusOverride } from '@/hooks/useUpdateMdmStatusOverride';
import { useUserHasOrganizationPermission } from '@/hooks/useUserHasOrganizationPermission';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { skipToken } from '@reduxjs/toolkit/query';
import { CopyButton } from '@common';

export const DeviceNotEnrolledBanner = ({ asset }: { asset: Asset }) => {
  const { t } = useTranslation('assets');
  const organizationId = useGetOrganizationId();
  const { space_s, space_m } = spaces;
  const [buttonTextColor] = useThemeColors([ColorsV2.TEXT]);
  const { openModal, closeModal } = useGlobalUI();
  const { ithEnableNotApplicableMdmFlagForDevices } = useFlags();
  const { markNotApplicable, isLoading: isMarkingNotApplicable } =
    useUpdateMdmStatusOverride(organizationId);
  const canModifyAssets = useUserHasOrganizationPermission(
    PermissionEnum.Assetsupdate
  );
  const hasMdmEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );
  const { data: employee } = useGetEmployeeQuery(
    asset.assignedTo
      ? {
          employeeId: asset.assignedTo,
          organizationId: organizationId,
        }
      : skipToken
  );

  return (
    <Banner
      intent="error"
      isFlat
      title={t('assetDetailsContent.deviceNotManaged.title')}
      id="device-not-managed-banner"
      isFullWidth
      message={() => (
        <Flex vertical gap={space_m}>
          <Text variant="body-2">
            {t('assetDetailsContent.deviceNotManaged.message')}
          </Text>
          {!asset.assignedTo && (
            <Text variant="body-2">
              {t('assetDetailsContent.deviceNotManaged.assignMessage')}
            </Text>
          )}
        </Flex>
      )}
      cta={
        <Flex py={space_s} gap={space_s} alignItems="center">
          <Button
            id="enroll-this-device-button"
            variant="link"
            color={buttonTextColor}
            onClick={() => {
              window.open(
                getLocalizedSupportUrl(
                  'https://support.electric.ai/electricithub/electric-it-hub-mdm-enrollment'
                ),
                '_blank'
              );
            }}>
            {t('assetDetailsContent.deviceNotManaged.learnMore')}
          </Button>
          {!asset.assignedTo ? (
            <CopyButton
              id="copy-instructions-button"
              intent="danger"
              color={buttonTextColor}
              textToCopy={t(
                'assetDetailsContent.deviceNotManaged.instructions'
              )}>
              {t(
                'assetDetailsContent.deviceNotManaged.copyEnrollmentInstructions'
              )}
            </CopyButton>
          ) : (
            <Button
              id="enroll-this-device-button"
              variant="fill"
              intent="primary"
              disabled={!employee}
              onClick={() => {
                openModal(
                  <MDMInviteModalWrapper
                    employeesWithDevices={[
                      {
                        employee: employee!,
                        devices: [asset],
                      },
                    ]}
                    inviteVisible
                    handleModalClose={closeModal}
                  />
                );
              }}>
              {t('assetDetailsContent.deviceNotManaged.enrollThisDevice')}
            </Button>
          )}
          {ithEnableNotApplicableMdmFlagForDevices &&
            canModifyAssets &&
            hasMdmEntitlement && (
              <Button
                id="mark-not-applicable-button"
                variant="outline"
                intent="danger"
                color={buttonTextColor}
                disabled={isMarkingNotApplicable}
                onClick={() => markNotApplicable(asset)}>
                {t('assetDetailsContent.deviceNotManaged.markNotApplicable')}
              </Button>
            )}
        </Flex>
      }
    />
  );
};
